import React, { useEffect, useState, useRef, useMemo } from "react";
import Header from "./components/Header";
import { TdsSpinner, TdsToast } from "@scania/tegel-react";
import { getDistanceKey } from "./utils/distance";
import {
  selectDistanceMatrixCandidates,
  shouldAutoCalculateDistances,
  getDistanceCalculationLogType,
} from "./utils/distanceSelection";
import TruckInfoCard from "./components/TruckInfoCard";
import ActionButtons from "./components/ActionButtons";
import FiltersPanel from "./components/FiltersPanel";
import WorkshopList from "./components/WorkshopList";
import { MarkerClustererF } from "@react-google-maps/api";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  Autocomplete,
  DirectionsRenderer,
} from "@react-google-maps/api";
import tzLookup from "tz-lookup";
import { LatLng, FmsData, Dealer } from "./types/index";
import "./global.css";
import { OverlayView } from "@react-google-maps/api";
import { useAuth } from "./auth/useAuth";
import { makeClient } from "./api/client";
import {
  svgTruck,
  dealerWorkshopIcon,
  customerWorkshopIcon,
  externalCompanyIcon,
} from "./components/MapsIcons";

import { useMsal } from "@azure/msal-react";

//Fetch from env
const API_BASE = process.env.REACT_APP_API_URL!;

const hasValidCoords = (lat?: number | null, lng?: number | null) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180 &&
  !(lat === 0 && lng === 0);

// Centralized helper for accurate Google Distance Matrix calculation and caching
type CalculateAccurateDistancesParams = {
  origin: LatLng;
  destinations: (LatLng & { id?: string; name?: string })[];
  numberOfDestinations?: number;
  getDistanceKey: (origin: LatLng, dest: LatLng) => string;
  distanceCache: React.MutableRefObject<
    Record<
      string,
      { distanceText: string; distanceValue: number; durationText?: string }
    >
  >;
  onSuccess?: (
    elements: google.maps.DistanceMatrixResponse["rows"][0]["elements"],
    destinations: { id?: string; name?: string; lat: number; lng: number }[]
  ) => void;
  onError?: (status: any) => void;
  travelMode?: google.maps.TravelMode;
  reverse?: boolean;
};

function calculateAccurateDistances({
  origin,
  destinations,
  numberOfDestinations = 3,
  getDistanceKey,
  distanceCache,
  onSuccess,
  onError,
  travelMode = google.maps.TravelMode.DRIVING,
  reverse = false,
}: CalculateAccurateDistancesParams) {
  if (!window.google?.maps?.DistanceMatrixService) {
    onError?.("Google Maps JS not loaded");
    return;
  }
  // Only destinations that need calculation (not cached)
  const uncached = destinations.filter((dest) => {
    const key = getDistanceKey(origin, dest);
    return !distanceCache.current[key];
  });
  if (uncached.length === 0) {
    // Already cached, call success with empty elements
    onSuccess?.([], uncached);
    return;
  }
  // Only up to N destinations
  const top = uncached.slice(0, numberOfDestinations);
  // Use DistanceMatrixService
  const service = new window.google.maps.DistanceMatrixService();
  const request = reverse
    ? {
        origins: top.map((dest) => ({ lat: dest.lat, lng: dest.lng })),
        destinations: [origin],
        travelMode,
      }
    : {
        origins: [origin],
        destinations: top.map((dest) => ({ lat: dest.lat, lng: dest.lng })),
        travelMode,
      };
  service.getDistanceMatrix(request, (response, status) => {
    if (status === "OK") {
      if (reverse) {
        const rows = response?.rows ?? [];
        if (
          rows.length === top.length &&
          rows.every((row) => row?.elements?.[0])
        ) {
          const elements = rows.map((row) => row.elements[0]);
          // Save to cache
          top.forEach((dest, i) => {
            const el = elements[i];
            const text = el?.distance?.text;
            const value = el?.distance?.value;
            const duration = el?.duration?.text;
            const key = getDistanceKey(origin, dest);
            if (text && value !== undefined) {
              distanceCache.current[key] = {
                distanceText: text,
                distanceValue: value,
                durationText: duration,
              };
            }
          });
          onSuccess?.(elements, top);
          return;
        }
      } else if (response?.rows?.[0]?.elements?.length === top.length) {
        // Save to cache
        top.forEach((dest, i) => {
          const el = response.rows[0].elements[i];
          const text = el?.distance?.text;
          const value = el?.distance?.value;
          const duration = el?.duration?.text;
          const key = getDistanceKey(origin, dest);
          if (text && value !== undefined) {
            distanceCache.current[key] = {
              distanceText: text,
              distanceValue: value,
              durationText: duration,
            };
          }
        });
        onSuccess?.(response.rows[0].elements, top);
        return;
      }
    }
    onError?.(status);
  });
}

// Workshop Icons (Symbol factories)
const iconForSource = (source?: Dealer["source"]): google.maps.Symbol => {
  switch (source) {
    case "External":
      return externalCompanyIcon();
    case "Customer":
      return customerWorkshopIcon();
    case "Scania":
    default:
      return dealerWorkshopIcon();
  }
};

// Slightly spread markers that share identical coordinates
function jitterSameCoords(items: Dealer[]): Dealer[] {
  // group by exact lat/lng pair (string key)
  const groups = new Map<string, Dealer[]>();
  for (const w of items) {
    const key = `${w.latitude},${w.longitude}`;
    const arr = groups.get(key) || [];
    arr.push(w);
    groups.set(key, arr);
  }

  const result: Dealer[] = [];
  // entries with Array.from
  for (const [, group] of Array.from(groups.entries())) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    // spread in a small circle around the original point
    const baseLat = group[0].latitude;
    const baseLng = group[0].longitude;
    const n = group.length;
    const radiusMeters = 8 + Math.min(12, n * 2); // tiny, grows a bit with n

    // convert meters to degrees (roughly)
    const latMetersPerDeg = 111_320; // ~meters per degree latitude
    const lngMetersPerDeg = 111_320 * Math.cos((baseLat * Math.PI) / 180);

    const dLatPerMeter = 1 / latMetersPerDeg;
    const dLngPerMeter = 1 / (lngMetersPerDeg || latMetersPerDeg); // guard cos(90°)

    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n;
      const dLat = Math.sin(angle) * radiusMeters * dLatPerMeter;
      const dLng = Math.cos(angle) * radiusMeters * dLngPerMeter;

      const w = group[i];
      result.push({
        ...w,
        latitude: baseLat + dLat,
        longitude: baseLng + dLng,
      });
    }
  }
  return result;
}

// --- Truck marker with smooth entrance and relocation animation ---
const easeInOutQuad = (t: number) =>
  t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

type TruckMarkerProps = {
  position?: { lat: number; lng: number };
  icon: google.maps.Symbol;
  mapsReady: boolean;
  hidden?: boolean;
};

const TruckMarker: React.FC<TruckMarkerProps> = ({
  position,
  icon,
  mapsReady,
  hidden,
}) => {
  const [animPos, setAnimPos] = React.useState(position ?? { lat: 0, lng: 0 });

  // Keep latest animated position (for mid-flight retargeting)
  const animPosRef = React.useRef(animPos);
  useEffect(() => {
    animPosRef.current = animPos;
  }, [animPos]);

  // Track current animation target to avoid duplicate starts in StrictMode
  const targetRef = React.useRef(position ?? { lat: 0, lng: 0 });

  const rafRef = React.useRef<number | null>(null);
  const firstPaintRef = React.useRef(true);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Animate on position change, starting from *current* on-screen pos
  React.useLayoutEffect(() => {
    if (!position) {
      return;
    }
    const next = position;

    if (
      targetRef.current.lat === next.lat &&
      targetRef.current.lng === next.lng
    ) {
      return;
    }
    targetRef.current = next;

    const startPos = animPosRef.current;
    const DURATION = 650;
    const start = performance.now();

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION);
      const eased = easeInOutQuad(t);
      const lat = lerp(startPos.lat, next.lat, eased);
      const lng = lerp(startPos.lng, next.lng, eased);
      setAnimPos({ lat, lng });

      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    };

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  }, [position?.lat, position?.lng]);

  // Disable DROP after first paint
  useEffect(() => {
    firstPaintRef.current = false;
  }, []);

  const invalid =
    !position ||
    position.lat === undefined ||
    position.lng === undefined ||
    (position.lat === 0 && position.lng === 0);

  if (hidden || invalid) return null;

  return (
    <Marker
      position={animPos}
      icon={icon}
      animation={
        mapsReady && window.google?.maps?.Animation && firstPaintRef.current
          ? window.google.maps.Animation.DROP
          : undefined
      }
      zIndex={1000}
    />
  );
};

const highwayStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      { color: "#0057ff" }, // CHANGE THIS COLOR TO WHATEVER YOU WANT
      { weight: 1 },
    ], //#0057ff
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [
      { color: "#ff6600" }, // NATIONAL HIGHWAYS COLOR (change if you like)
      { weight: 1 },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  // Force POIs/businesses to stay visible 
  { featureType: "poi", stylers: [{ visibility: "on" }] },
  { featureType: "poi.business", stylers: [{ visibility: "on" }] },
  { featureType: "poi.park", stylers: [{ visibility: "on" }] },
];

function App() {
  const { accessToken, account, login, ready } = useAuth();
  // disable Send to Eagle button while posting
  const [isSendingToEagle, setIsSendingToEagle] = useState(false);
  const fetchWithAuth = makeClient(accessToken);
  const { inProgress } = useMsal();

  // Trigger login via effect to avoid conditional hook order issues
  useEffect(() => {
    if (ready && !account && inProgress === "none") {
      login();
    }
  }, [ready, account, login, inProgress]);

  const positionToastRef = useRef<HTMLTdsToastElement>(null);
  const [showPositionToast, setShowPositionToast] = useState(false);
  const eagleToastRef = useRef<HTMLTdsToastElement>(null);
  const [showEagleToast, setShowEagleToast] = useState(false);
  // Failed fetch toast for workshop endpoints
  const failedFetchToastRef = useRef<HTMLTdsToastElement>(null);
  const [showFailedFetchToast, setShowFailedFetchToast] = useState(false);
  // Workshop not found toast state
  const [showWorkshopNotFoundToast, setShowWorkshopNotFoundToast] =
    useState(false);
  const notFoundToastRef = useRef<HTMLTdsToastElement>(null);
  // Track if the not-found toast has already been shown for this workshopId
  const toastAlreadyShownForWorkshop = useRef<string | null>(null);
  // Parse URL params
  const {
    caseNumber,
    vin: vinFromUrl,
    fuelType,
    vehicleType,
    urlLat,
    urlLng,
    selectedWorkshopId,
  } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const latRaw = params.get("lat");
    const lngRaw = params.get("lng");
    // Treat empty string as not present (null)
    const latValue = latRaw === "" ? null : latRaw;
    const lngValue = lngRaw === "" ? null : lngRaw;
    const lat = latValue !== null ? Number(latValue) : null;
    const lng = lngValue !== null ? Number(lngValue) : null;
    const isValidCoord =
      lat !== null &&
      lng !== null &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;

    return {
      caseNumber: params.get("caseNumber") || "Unknown",
      vin: params.get("vin"),
      fuelType:
        params.get("fuelType")?.toLowerCase() ??
        params.get("fueltype")?.toLowerCase() ??
        null,
      vehicleType:
        params.get("type") ??
        params.get("vehicleType") ??
        params.get("vehicletype") ??
        null,
      urlLat: isValidCoord ? lat : null,
      urlLng: isValidCoord ? lng : null,
      // support either ?workshopId= or ?dealerId= or ?workshopid or ?dealerid
      selectedWorkshopId:
        params.get("workshopId") ??
        params.get("dealerId") ??
        params.get("workshopid") ??
        params.get("dealerid") ??
        null,
    };
  }, []);

  useEffect(() => {
    const cn = caseNumber && caseNumber !== "Unknown" ? caseNumber : null;
    document.title = cn ? `Maps ${cn}` : "Eagle Maps";
  }, [caseNumber]);

  const googleMapLibraries = useMemo<
    ("places" | "drawing" | "geometry" | "visualization")[]
  >(() => ["places"], []);

  // one-time guard for URL workshop selection
  const appliedWorkshopFromUrl = useRef(false);
  const autoRouteFromUrl = useRef(false);
  // Track if this is the initial position load (for auto-calculation)
  const isInitialPositionLoad = useRef(false);

  const [position, setPosition] = useState<FmsData | null>(null);
  const distanceMatrixCallCount = useRef(0);
  const isCalculatingDistances = useRef(false);
  const [pendingMove, setPendingMove] = useState<google.maps.LatLng | null>(
    null
  );
  const previousWorkshopState = useRef<{
    position: { lat: number; lng: number };
    dealers: Dealer[];
  } | null>(null);

  // resizable sidebar
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem("sidebarWidthPx"));
    return Number.isFinite(saved) && saved >= 260 ? saved : 700;
  });
  const isResizing = useRef(false);

  // Start resize on mousedown
  const onResizerMouseDown = () => {
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Global mousemove/mouseup to track dragging
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizing.current || !layoutRef.current) return;
      const rect = layoutRef.current.getBoundingClientRect();
      // new width = mouse X relative to the left edge of the layout container
      let w = e.clientX - rect.left;
      // clamp to sensible range
      w = Math.max(260, Math.min(700, w));
      setSidebarWidth(w);
    };

    const onUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      localStorage.setItem("sidebarWidthPx", String(sidebarWidth));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [sidebarWidth]);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [workshops, setWorkshops] = useState<Dealer[]>([]);
  const [expandedDealerId, setExpandedDealerId] = useState<string | null>(null);
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
  const [pinnedWorkshopId, setPinnedWorkshopId] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<
    ("Scania" | "External" | "Customer")[]
  >(["Scania"]);
  const [address, setAddress] = useState<string | null>(null);
  const [manualAddress, setManualAddress] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const distanceCache = useRef<
    Record<
      string,
      { distanceText: string; distanceValue: number; durationText?: string }
    >
  >({});
  const searchRadius = useRef(250); // km
  const pageSize = useRef(10); // how many WS to ask per endpoint
  const maxRadius = 1500; // optional cap
  const [refreshUI, setRefreshUI] = useState(0);
  const [showOnlyOpenNow, setShowOnlyOpenNow] = useState(false); //Open workshop checkbox filter
  const [rawWorkshops, setRawWorkshops] = useState<Dealer[]>([]);
  const [isLoadingWorkshops, setIsLoadingWorkshops] = useState(true);
  const [positionFetchFailed, setPositionFetchFailed] = useState(false);
  const [directionsResult, setDirectionsResult] =
    useState<google.maps.DirectionsResult | null>(null);
  const [distanceCalculated, setDistanceCalculated] = useState(false);
  const fuelCodeMap: Record<string, string> = {
    electric: "electric",
    hybrid: "hybrid",
    lng: "lng",
    cng: "cng",
  };
  const vehicleTypeCodeMap: Record<string, string> = {
    truck: "truckservice",
    bus: "busservice",
    trailer: "trailerservice",
    industrialengine: "industrialpowergenerationenginesservice",
    marineengine: "marineenginesservice",
  };
  const vehicleTypeLabels: Record<string, string> = {
    truckservice: "Truck",
    busservice: "Bus",
    trailerservice: "Trailer",
    industrialpowergenerationenginesservice: "Industrial Engine",
    marineenginesservice: "Marine Engine",
  };
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
    if (!vehicleType) return [];
    const key = vehicleType.toLowerCase().replace(/\s+/g, "");
    const mapped = vehicleTypeCodeMap[key];
    return mapped ? [mapped] : [];
  });
  const [selectedFuels, setSelectedFuels] = useState<string[]>(
    fuelType && fuelCodeMap[fuelType] ? [fuelCodeMap[fuelType]] : []
  );
  const [mapsReady, setMapsReady] = useState(false);
  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (selectedSources.length) parts.push(`WS: ${selectedSources.join(", ")}`);
    if (selectedTypes.length) {
      const labels = selectedTypes
        .map((code) => vehicleTypeLabels[code] || code)
        .filter(Boolean);
      parts.push(`Type: ${labels.join(", ")}`);
    }
    if (selectedServices.length) parts.push(`Service: ${selectedServices.length}`);
    if (selectedFuels.length) parts.push(`Fuel: ${selectedFuels.join(", ")}`);
    if (showOnlyOpenNow) parts.push("Open now");
    return parts;
  }, [
    selectedSources,
    selectedTypes,
    selectedServices,
    selectedFuels,
    showOnlyOpenNow,
    vehicleTypeLabels,
  ]);

  // Expand/Collapse Filters
  const [filtersOpen, setFiltersOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem("filtersOpen");
    return saved ? saved === "1" : true; // default pinned on first load
  });
  const [filtersHovering, setFiltersHovering] = useState(false);
  const filtersVisible = filtersOpen || filtersHovering;
  // Collapsible Truck Info Section
  const [truckInfoOpen, setTruckInfoOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem("truckInfoOpen");
    return saved ? saved === "1" : true; // default pinned on first load
  });
  const [truckHovering, setTruckHovering] = useState(false);
  const truckInfoVisible = truckInfoOpen || truckHovering;
  useEffect(() => {
    localStorage.setItem("truckInfoOpen", truckInfoOpen ? "1" : "0");
  }, [truckInfoOpen]);
  useEffect(() => {
    localStorage.setItem("filtersOpen", filtersOpen ? "1" : "0");
  }, [filtersOpen]);

  useEffect(() => {
    console.log("🔍 URL fuelType param:", fuelType);
    console.log("✅ Initial selectedFuels:", selectedFuels);
  }, []);

  // Enable distance calculation button when filters change
  useEffect(() => {
    setDistanceCalculated(false);
  }, [selectedSources, selectedTypes, selectedServices, selectedFuels, showOnlyOpenNow]);

  const isOpenNow = (dealer: Dealer): boolean => {
    if (!dealer.openingHours || !position) return false;

    try {
      const timeZone = tzLookup(position.gpsLatitude, position.gpsLongitude);

      const now = new Date();
      const [hour, minute] = now
        .toLocaleTimeString("en-GB", {
          timeZone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .split(":")
        .map(Number);

      const localDay = now
        .toLocaleDateString("en-GB", { timeZone, weekday: "long" })
        .toUpperCase();

      const currentMinutes = hour * 60 + minute;

      return dealer.openingHours.some(
        (entry) =>
          entry.days.includes(localDay) &&
          entry.openTimes.some((time) => {
            const [fromH, fromM] = time.timeFrom.split(":").map(Number);
            const [toH, toM] = time.timeTo.split(":").map(Number);
            const from = fromH * 60 + fromM;
            const to = toH * 60 + toM;
            return currentMinutes >= from && currentMinutes <= to;
          })
      );
    } catch (err) {
      console.warn(
        "⚠️ Failed to determine timezone for workshop filtering.",
        err
      );
      return false;
    }
  };

  // Map of fuel types to required service codes
  const fuelServiceMap: Record<string, string[]> = {
    electric: [
      "e-mobilityvcbbatteryrepairserviceworkshop",
      "e-mobilityvcbserviceworkshop",
    ],
    hybrid: [
      "e-mobilityvcbbatteryrepairserviceworkshop",
      "e-mobilityvcbserviceworkshop",
    ],
    lng: ["gasvehicleservicelng"],
    cng: ["gasvehicleservicecng"],
  };

  //defaultCenter computed from the URL
  const defaultCenter = useMemo(() => {
    if (urlLat !== null && urlLng !== null) {
      return { lat: urlLat, lng: urlLng };
    }
    // Fallback default center: Stockholm
    return { lat: 59.3293, lng: 18.0686 }; // Stockholm
  }, [urlLat, urlLng]);

  const computedCenter = useMemo<google.maps.LatLngLiteral | undefined>(() => {
    // If a workshop is selected, stop controlling center (let the map keep whatever the user panned to)
    if (selectedDealerId) return undefined;
    // If we have a valid truck position, use it; otherwise fallback to defaultCenter (Stockholm)
    if (
      position &&
      hasValidCoords(position.gpsLatitude, position.gpsLongitude)
    ) {
      return { lat: position.gpsLatitude, lng: position.gpsLongitude };
    }
    return defaultCenter;
  }, [selectedDealerId, position, defaultCenter]);

  const filteredWorkshops = useMemo(() => {
    if (!position) return []; // ✅ avoid using position! before it exists
    const filtered = rawWorkshops
      .map((w) => {
        const cached =
          distanceCache.current[
            getDistanceKey(
              { lat: position.gpsLatitude, lng: position.gpsLongitude },
              { lat: w.latitude, lng: w.longitude }
            )
          ];
        return {
          ...w,
          distanceText: cached?.distanceText ?? w.distanceText,
          distanceValue: cached?.distanceValue ?? w.distanceValue,
          durationText: cached?.durationText ?? w.durationText,
        };
      })
      .filter((dealer) => {
        const matchesType =
          selectedTypes.length === 0 ||
          selectedTypes.some((s) => dealer.serviceCodes.includes(s));

        const matchesService =
          selectedServices.length === 0 ||
          selectedServices.some((s) => dealer.serviceCodes.includes(s));

        const matchesSource =
          selectedSources.length === 0 ||
          (dealer.source && selectedSources.includes(dealer.source));

        const matchesOpenNow = !showOnlyOpenNow || isOpenNow(dealer);

        const matchesFuel =
          selectedFuels.length === 0 ||
          selectedFuels.some((fuel) => {
            const requiredCodes = fuelServiceMap[fuel.toLowerCase()];
            return (
              requiredCodes &&
              requiredCodes.some((code) =>
                dealer.serviceCodes.map((s) => s.toLowerCase()).includes(code)
              )
            );
          });

        return (
          matchesService &&
          matchesType &&
          matchesSource &&
          matchesOpenNow &&
          matchesFuel
        );
      });

    return filtered.sort((a, b) => {
      if (a.distanceValue == null && b.distanceValue == null) return 0;
      if (a.distanceValue == null) return 1;
      if (b.distanceValue == null) return -1;
      return a.distanceValue - b.distanceValue;
    });
  }, [
    rawWorkshops,
    selectedServices,
    selectedSources,
    showOnlyOpenNow,
    selectedTypes,
    selectedFuels,
    position,
    refreshUI,
  ]);

  // After filteredWorkshops useMemo:
  const jitteredWorkshops = useMemo(
    () => jitterSameCoords(filteredWorkshops),
    [filteredWorkshops]
  );

  useEffect(() => {
    if (!expandedDealerId || !mapRef.current) return;
    const selected = jitteredWorkshops.find(
      (w) => w.dealerId === expandedDealerId
    );
    if (selected) {
      mapRef.current.panTo({
        lat: selected.latitude,
        lng: selected.longitude,
      });
    }
  }, [expandedDealerId, jitteredWorkshops]);

  // 👉 NEW: auto-fit truck + selected workshop into view
  useEffect(() => {
    if (!mapRef.current) return;
    if (!position) return;
    if (!selectedDealerId) return;

    // use jittered coordinates so we fit to the *clicked* marker’s location
    const dealer = jitteredWorkshops.find(
      (w) => w.dealerId === selectedDealerId
    );
    if (!dealer) return;

    if (!window.google?.maps?.LatLngBounds) return;

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: position.gpsLatitude, lng: position.gpsLongitude });
    bounds.extend({ lat: dealer.latitude, lng: dealer.longitude });

    mapRef.current.fitBounds(bounds, 80);
  }, [selectedDealerId, position, jitteredWorkshops]);

  const fetchPositionLocation = (isInitialLoad = false) => {
    if (!ready || !account) {
      return; // wait until auth is ready to avoid unauthenticated calls
    }
    setShowPositionToast(true);
    setTimeout(() => {
      if (positionToastRef.current) positionToastRef.current.showToast();
    }, 0);
    const params = new URLSearchParams(window.location.search);
    const vin = params.get("vin");
    if (!vin) {
      console.warn("❌ VIN not provided in URL.");
      setShowPositionToast(false);
      setTimeout(() => {
        if (positionToastRef.current) positionToastRef.current.hideToast();
      }, 100);
      // Recenter over Stockholm and clear any route/selection
      setPosition(null);
      setSelectedDealerId(null);
      setExpandedDealerId(null);
      setDirectionsResult(null);
      setAddress(null);
      setManualAddress(null);
      mapRef.current?.panTo(defaultCenter);
      return;
    }
    //console.log(API_BASE);
    fetchWithAuth(`${API_BASE}/api/cdc/position/${vin}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch position data.");
        return res.json();
      })
      .then((data) => {
        const newPosition = data?.data ?? data;

        if (!newPosition) {
          console.warn("🚚 No truck position found.");
          setShowPositionToast(false);
          setTimeout(() => {
            if (positionToastRef.current) positionToastRef.current.hideToast();
          }, 100);
          // Recenter over Stockholm and clear any route/selection
          setPosition(null);
          setSelectedDealerId(null);
          setExpandedDealerId(null);
          setDirectionsResult(null);
          setAddress(null);
          setManualAddress(null);
          mapRef.current?.panTo(defaultCenter);
          return;
        }

        // If the position service returned invalid coords (including 0,0), reset to default center
        if (
          !hasValidCoords(newPosition.gpsLatitude, newPosition.gpsLongitude)
        ) {
          console.warn(
            "⚠️ Position service returned invalid coordinates; recentering to default."
          );
          setPosition(null);
          setAddress(null);
          mapRef.current?.panTo(defaultCenter);
          setShowPositionToast(false);
          setTimeout(() => {
            if (positionToastRef.current) positionToastRef.current.hideToast();
          }, 100);
          return;
        }

        setSelectedDealerId(null);
        setExpandedDealerId(null);
        setDirectionsResult(null);

        // Clear the selected workshop so computedCenter becomes the truck position again and the map recenters automatically.
        setPosition(newPosition);
        setManualAddress(null);
        setDistanceCalculated(false);
        // Set auto-calculation flag based on whether this is initial load or manual fetch
        isInitialPositionLoad.current = isInitialLoad;

        if (
          hasValidCoords(newPosition.gpsLatitude, newPosition.gpsLongitude) &&
          window.google?.maps?.Geocoder
        ) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            {
              location: {
                lat: newPosition.gpsLatitude,
                lng: newPosition.gpsLongitude,
              },
            },
            (results, status) => {
              if (status === "OK" && results && results.length > 0) {
                setAddress(results[0].formatted_address);
              } else {
                setAddress(null);
              }
            }
          );
        } else {
          setAddress(null);
        }
        setShowPositionToast(false);
        setTimeout(() => {
          if (positionToastRef.current) positionToastRef.current.hideToast();
        }, 100);
      })
      .catch((err) => {
        console.error("❌ Failed to fetch position:", err.message);
        setPosition(null);
        setPositionFetchFailed(true);
        setShowPositionToast(false);
        setTimeout(() => {
          if (positionToastRef.current) positionToastRef.current.hideToast();
        }, 100);
        setSelectedDealerId(null);
        setExpandedDealerId(null);
        setDirectionsResult(null);
        setAddress(null);
        setManualAddress(null);
        mapRef.current?.panTo(defaultCenter);
      });
  };

  const handleRightClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    setPendingMove(e.latLng);
  };

  // Initial position (URL if valid, else position service)
  useEffect(() => {
    if (!ready || !account) return; // wait for authentication before initial fetch
    const setAddressFromLatLng = (lat: number, lng: number) => {
      if (!hasValidCoords(lat, lng) || !window.google?.maps?.Geocoder) {
        setAddress(null);
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          setAddress(results[0].formatted_address);
          setManualAddress(null);
        } else {
          setAddress(null);
        }
      });
    };

    // Don’t try reverse geocoding until the Maps script is ready
    if (!mapsReady) return;

    // Only use seeded lat/lng if both are present and not null
    if (urlLat !== null && urlLng !== null) {
      // Seed position from URL and pan the map
      const seeded = {
        gpsLatitude: urlLat,
        gpsLongitude: urlLng,
        externalEquipmentReference: vinFromUrl ?? "No VIN Provided",
        gpsTimestamp: new Date().toISOString(),
      };
      setPosition(seeded);
      setPositionFetchFailed(false);
      setDistanceCalculated(false);
      // This is initial load, allow auto-calculation
      isInitialPositionLoad.current = true;
      // Now that Maps is ready, do the reverse geocode
      setAddressFromLatLng(urlLat, urlLng);
    } else {
      fetchPositionLocation(true); // true = this is initial load, allow auto-calculation
    }
  }, [urlLat, urlLng, vinFromUrl, mapsReady, ready, account]);

  const confirmMove = () => {
    if (!pendingMove) return;

    const newLat = pendingMove.lat();
    const newLng = pendingMove.lng();

    // reset any previous selection/route when changing truck location
    setSelectedDealerId(null);
    setExpandedDealerId(null);
    setDirectionsResult(null);

    // Update marker position
    setPosition((prev) =>
      prev
        ? {
            ...prev,
            gpsLatitude: newLat,
            gpsLongitude: newLng,
            gpsTimestamp: new Date().toISOString(),
          }
        : {
            gpsLatitude: newLat,
            gpsLongitude: newLng,
            externalEquipmentReference: "Unknown",
            gpsTimestamp: new Date().toISOString(),
          }
    );
    previousWorkshopState.current = null; // ✅ Force refetch based on manual move
    setDistanceCalculated(false);

    // Reverse geocode
    if (hasValidCoords(newLat, newLng) && window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: newLat, lng: newLng } },
        (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            setAddress(results[0].formatted_address);
            setManualAddress(null);
          } else {
            setAddress(null);
          }
        }
      );
    } else {
      setAddress(null);
    }

    setPendingMove(null);
  };

  const cancelMove = () => setPendingMove(null);

  const widenSearch = () => {
    // bump radius and items per endpoint, with sane caps
    if (searchRadius.current < maxRadius) {
      searchRadius.current = Math.min(maxRadius, searchRadius.current + 50);
    }
    pageSize.current = Math.min(100, pageSize.current + 10);
    previousWorkshopState.current = null; // force refetch regardless of same position
    if (position) {
      // trigger the workshops useEffect by nudging position with a shallow copy
      setPosition({ ...position });
    }
  };

  const resetSearch = () => {
    // restore defaults and refetch
    searchRadius.current = 250; // default radius
    pageSize.current = 10; // default page size
    previousWorkshopState.current = null; // force refetch even if position unchanged
    if (position) {
      setPosition({ ...position }); // trigger workshops useEffect
    }
  };

  // Fetch workshops when position changes (guarded by Maps readiness)
  useEffect(() => {
    setIsLoadingWorkshops(true);
    if (!position) {
      setIsLoadingWorkshops(false);
      return;
    }
    if (!hasValidCoords(position.gpsLatitude, position.gpsLongitude)) {
      setIsLoadingWorkshops(false);
      return;
    }
    if (!window.google?.maps?.Geocoder) {
      setIsLoadingWorkshops(false);
      return; // ✅ guard until Maps JS is ready
    }

    const currentPos = {
      lat: position.gpsLatitude,
      lng: position.gpsLongitude,
    };

    // Only fetch if position changes, not if only distanceCalculated changes.
    if (
      previousWorkshopState.current &&
      previousWorkshopState.current.position.lat === currentPos.lat &&
      previousWorkshopState.current.position.lng === currentPos.lng
    ) {
      setWorkshops(previousWorkshopState.current.dealers);
      setIsLoadingWorkshops(false);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat: position.gpsLatitude, lng: position.gpsLongitude };

    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status !== "OK" || !results || results.length === 0) {
        console.error("❌ Failed to reverse geocode truck position.");
        setIsLoadingWorkshops(false);
        return;
      }

      const countryComponent = results[0].address_components.find((c) =>
        c.types.includes("country")
      );
      const countryCode = countryComponent?.short_name ?? "SE";

      const endpoints = [
        {
          url: `${API_BASE}/api/workshop/GetNearbyScaniaDealers?lat=${position.gpsLatitude}&lng=${position.gpsLongitude}&page=${pageSize.current}&offset=0&radiusKm=${searchRadius.current}`,
          source: "Scania" as Dealer["source"],
        },
        {
          url: `${API_BASE}/api/workshop/GetExternalWorkshops?lat=${position.gpsLatitude}&lng=${position.gpsLongitude}&page=${pageSize.current}&offset=0&radiusKm=${searchRadius.current}`,
          source: "External" as Dealer["source"],
        },
        {
          url: `${API_BASE}/api/workshop/GetCustomerWorkshops?lat=${position.gpsLatitude}&lng=${position.gpsLongitude}&page=${pageSize.current}&offset=0&radiusKm=${searchRadius.current}`,
          source: "Customer" as Dealer["source"],
        },
      ];

      Promise.all(
        endpoints.map(async ({ url, source }) => {
          try {
            const res = await fetchWithAuth(url);
            if (!res.ok) {
              const text = await res.text();
              console.error(`❌ Fetch failed for ${url}:`, text);
              return null; // Explicitly return null on error
            }
            const data = await res.json();
            return data.map((dealer: Dealer) => ({
              ...dealer,
              source, // ensure this overrides any existing value
              country: countryCode, // add country info from reverse geocode
            }));
          } catch (err) {
            console.error(`❌ Exception while fetching ${url}:`, err);
            return null;
          }
        })
      )
        .then(([scania, external, customer]) => {
          if (!scania || !external || !customer) {
            setShowFailedFetchToast(true);
            setTimeout(() => {
              failedFetchToastRef.current?.showToast();
            }, 0);
            setIsLoadingWorkshops(false);
            return;
          }
          const allRaw = [...scania, ...external, ...customer];
          // Deduplicate by dealerId, preferring first occurrence (Scania > External > Customer)
          const seen = new Set();
          const all = allRaw.filter((dealer) => {
            if (seen.has(dealer.dealerId)) return false;
            seen.add(dealer.dealerId);
            return true;
          });

          if (all.length < 3 && searchRadius.current < maxRadius) {
            searchRadius.current = Math.min(maxRadius, searchRadius.current + 25);
            console.warn(
              `🔁 Fewer than 3 workshops found (${all.length}). Retrying with radius: ${searchRadius.current}km`
            );
            // Keep the loading UI steady while the next retry fetch starts.
            setTimeout(() => setPosition({ ...position }), 0);
            return;
          }

          // Set workshops immediately for UI display
          setWorkshops(all);
          setRawWorkshops(all);
          // Save to previousWorkshopState for performance
          previousWorkshopState.current = {
            position: currentPos,
            dealers: all,
          };
          setIsLoadingWorkshops(false);
        })
        .catch(() => {
          setIsLoadingWorkshops(false);
        });
    });
  }, [position]);

  // Distance Matrix helper — ONLY for filtered workshops
  const runDistanceCalculation = React.useCallback((calculationMode: "automatic" | "manual" = "manual") => {
    if (!position) return;

    // Guard to avoid re-entrancy
    if (isCalculatingDistances.current) return;
    isCalculatingDistances.current = true;

    // Wait for workshops to finish loading before calculating distances
    if (isLoadingWorkshops || rawWorkshops.length === 0) {
      console.warn("⚠️ Workshops still loading or empty. Skipping distance calculation.");
      isCalculatingDistances.current = false;
      setDistanceCalculated(false);
      return;
    }

    const origin = {
      lat: position.gpsLatitude,
      lng: position.gpsLongitude,
    };

    // Work only with the filtered set visible in the UI
    const candidates = filteredWorkshops;
    if (!candidates || candidates.length === 0) {
      isCalculatingDistances.current = false;
      setDistanceCalculated(false); // Reset button state since no calculation was performed
      return;
    }

    const closest = selectDistanceMatrixCandidates({
      origin,
      candidates,
      selectedDealerId,
      distanceCache: distanceCache.current,
      maxDestinations: 3,
      calculationMode,
    });

    if (closest.length === 0) {
      console.warn(
        "⚠️ No valid destinations for Distance Matrix. Skipping API call."
      );
      isCalculatingDistances.current = false;
      setDistanceCalculated(false); // Reset button state since no calculation was performed
      return;
    }

    distanceMatrixCallCount.current += 1;
    console.log(
      `📊 Google Distance Matrix API call #${distanceMatrixCallCount.current}`
    );
    console.log("🚚 Origin:", origin);
    console.log(
      "🎯 Filtered destinations:",
      closest.map((w) => ({ lat: w.latitude, lng: w.longitude, name: w.name }))
    );

    calculateAccurateDistances({
      origin,
      destinations: closest.map((w) => ({
        lat: w.latitude,
        lng: w.longitude,
        id: w.dealerId,
        name: w.name,
      })),
      numberOfDestinations: Math.min(3, closest.length),
      getDistanceKey,
      distanceCache,
      reverse: true,
      onSuccess: () => {
        // Refresh displayed distances for the entire set from cache (both filtered and non-filtered)
        const updatedAll = rawWorkshops.map((w) => {
          const key = getDistanceKey(origin, {
            lat: w.latitude,
            lng: w.longitude,
          });
          const cached = distanceCache.current[key];
          return cached
            ? {
                ...w,
                distanceText: cached.distanceText,
                distanceValue: cached.distanceValue,
                durationText: cached.durationText,
              }
            : w;
        });

        setRawWorkshops(updatedAll); // keep raw list enriched with any newly cached values
        setWorkshops(updatedAll); // keep UI list in sync for components reading `workshops`
        setRefreshUI((r) => r + 1); // nudge WorkshopList items to refresh

        previousWorkshopState.current = {
          position: origin,
          dealers: updatedAll,
        };

        // mark calculation complete
        isCalculatingDistances.current = false;
        setDistanceCalculated(true);
      },
      onError: (status) => {
        console.error("❌ Distance Matrix failed:", status);
        // allow retry on error
        isCalculatingDistances.current = false;
        setDistanceCalculated(false);
      },
    });
  }, [position, filteredWorkshops, rawWorkshops, selectedDealerId]);

  // Log distance calculation to CloudWatch via API
  const logDistanceCalculation = React.useCallback(
    async (triggerType: "manual" | "automatic") => {
      if (!position || !ready || !account) return;

      try {
        await fetchWithAuth(`${API_BASE}/api/logs/distance-calculation`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            caseNumber: caseNumber,
            triggerType: triggerType,
            vin: vinFromUrl ?? position?.externalEquipmentReference ?? null,
            latitude: position.gpsLatitude,
            longitude: position.gpsLongitude,
          }),
        });
      } catch (err) {
        // Silently fail - logging shouldn't break the app
        console.warn("Failed to log distance calculation:", err);
      }
    },
    [position, caseNumber, vinFromUrl, ready, account, fetchWithAuth]
  );

  // Calculate distances when user clicks the button
  const handleCalculateDistancesClick = React.useCallback(
    (eventOrIsAutomatic?: any) => {
      // Handle both onClick event and manual calls
      const isAutomatic = typeof eventOrIsAutomatic === 'boolean' ? eventOrIsAutomatic : false;
      if (!position) return;
      const logType = getDistanceCalculationLogType({
        isAutomatic,
        distanceCalculated,
        isCalculating: isCalculatingDistances.current,
      });

      if (distanceCalculated || isCalculatingDistances.current) {
        if (logType) {
          logDistanceCalculation(logType);
        }
        return;
      }

      if (logType) {
        // Log to CloudWatch first
        logDistanceCalculation(logType);
      }

      // Don't set state here - let onSuccess/onError handle it to avoid race conditions
      runDistanceCalculation(isAutomatic ? "automatic" : "manual");
    },
    [position, distanceCalculated, runDistanceCalculation, logDistanceCalculation]
  );

  // Auto-run distance calculation once per position after workshops have loaded
  // Only auto-calculate on initial load, not when position changes manually
  useEffect(() => {
    const urlSelectedWorkshop = selectedWorkshopId
      ? workshops.find((w) => w.dealerId === selectedWorkshopId)
      : null;

    // When a workshop is provided in URL, wait until both selection and source
    // filter are applied so the first automatic calculation includes it.
    if (
      urlSelectedWorkshop &&
      (selectedDealerId !== urlSelectedWorkshop.dealerId ||
        (urlSelectedWorkshop.source &&
          !selectedSources.includes(urlSelectedWorkshop.source)))
    ) {
      return;
    }

    if (
      !shouldAutoCalculateDistances({
        position,
        distanceCalculated,
        isLoadingWorkshops,
        rawWorkshopsLength: rawWorkshops.length,
        isInitialPositionLoad: isInitialPositionLoad.current,
      })
    ) {
      return;
    }

    handleCalculateDistancesClick(true); // true = automatic trigger
    // Mark that initial calculation has been triggered
    isInitialPositionLoad.current = false;
  }, [
    position,
    rawWorkshops,
    distanceCalculated,
    isLoadingWorkshops,
    handleCalculateDistancesClick,
    selectedWorkshopId,
    workshops,
    selectedDealerId,
    selectedSources,
  ]);

  // Apply URL-selected workshop once
  useEffect(() => {
    if (!selectedWorkshopId) return; // no id in URL
    if (workshops.length === 0) return; // wait for data

    const match = workshops.find((w) => w.dealerId === selectedWorkshopId);

    // Only show the not-found toast on first load for a given workshopId from the URL
    if (
      selectedWorkshopId &&
      !match &&
      toastAlreadyShownForWorkshop.current !== selectedWorkshopId
    ) {
      setShowWorkshopNotFoundToast(true);
      toastAlreadyShownForWorkshop.current = selectedWorkshopId;
      setTimeout(() => {
        if (notFoundToastRef.current) notFoundToastRef.current.showToast();
      }, 0);
    } else if (
      !selectedWorkshopId ||
      !!match
    ) {
      setShowWorkshopNotFoundToast(false);
      setTimeout(() => {
        if (notFoundToastRef.current) notFoundToastRef.current.hideToast();
      }, 0);
    }

    if (appliedWorkshopFromUrl.current || !match) return;

    // Ensure the workshop source is visible before selecting/pinning.
    if (match.source && !selectedSources.includes(match.source)) {
      setSelectedSources((prev) =>
        prev.includes(match.source!) ? prev : [...prev, match.source!]
      );
      return;
    }

    setSelectedDealerId(match.dealerId);
    setExpandedDealerId(match.dealerId);
    setPinnedWorkshopId(match.dealerId);
    appliedWorkshopFromUrl.current = true;
    // keep your "don't force recenter on zoom" behavior, but pan once to it:
    mapRef.current?.panTo({ lat: match.latitude, lng: match.longitude });
    autoRouteFromUrl.current = true;
  }, [selectedWorkshopId, workshops, selectedSources]);

  // Only pin the URL-selected workshop on initial load; clear after any other selection.
  useEffect(() => {
    if (!pinnedWorkshopId) return;
    if (selectedDealerId !== pinnedWorkshopId) {
      setPinnedWorkshopId(null);
    }
  }, [selectedDealerId, pinnedWorkshopId]);

  useEffect(() => {
    if (!autoRouteFromUrl.current) return;
    if (!position) return;
    if (!selectedDealerId) return;

    drawRouteToWorkshop();
    autoRouteFromUrl.current = false;
  }, [position, selectedDealerId]);


  // Clear stale selection if it drops out of the filtered list (e.g., after moving or changing filters)
  useEffect(() => {
    if (
      selectedDealerId &&
      !filteredWorkshops.some((w) => w.dealerId === selectedDealerId)
    ) {
      setSelectedDealerId(null);
      setExpandedDealerId(null);
      setDirectionsResult(null);
      setDistanceCalculated(false);
    }
  }, [filteredWorkshops, selectedDealerId]);

  // Directions
  // Draw route to workshop (fetch directions and set in state)
  // Returns encoded polyline string or null
  const drawRouteToWorkshop = async (): Promise<string | null> => {
    setDirectionsResult(null);

    if (!position || !selectedDealerId) {
      alert("Truck position or workshop not selected.");
      return null;
    }

    const dealer = workshops.find((w) => w.dealerId === selectedDealerId);
    if (!dealer) {
      alert("No selected workshop found.");
      return null;
    }

    if (window.google?.maps?.DirectionsService) {
      const directionsService = new window.google.maps.DirectionsService();
      const origin = { lat: dealer.latitude, lng: dealer.longitude };
      const destination = {
        lat: position.gpsLatitude,
        lng: position.gpsLongitude,
      };

      try {
        const result = await new Promise<google.maps.DirectionsResult>(
          (resolve, reject) => {
            directionsService.route(
              {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result)
                  resolve(result);
                else reject(`Directions request failed due to ${status}`);
              }
            );
          }
        );

        setDirectionsResult(result);
        return result.routes?.[0]?.overview_polyline ?? null;
      } catch (error) {
        console.error("Failed to fetch directions:", error);
        return null;
      }
    }
    return null;
  };

  // --- Send to Eagle function with delay and toast (route first, then wait) ---
  const sendToEagle = async () => {
    // prevent double clicks
    if (isSendingToEagle) return;

    // disable the button now
    setIsSendingToEagle(true);

    // Only breakdown postion mode when NO workshop is selected
    if (!position) {
      alert("Missing truck position.");
      setIsSendingToEagle(false);
      return;
    }

    if (!selectedDealerId) {
      // Send truck-only payload instantly (no delay, no route)
      const payload = {
        CaseNumber: caseNumber,
        TruckLatitude: position.gpsLatitude,
        TruckLongitude: position.gpsLongitude,
        TruckGpsTimestamp: position.gpsTimestamp ?? new Date().toISOString(),

        // no workshop
        wsLatitude: null,
        wsLongitude: null,
        Workshop: {
          DealerId: "",
          Source: "",
        },

        StaticMapUrl: "",
        Country: "",
        Distance: "",
        DurationETA: "",
        ShortDescription: "Sent from EagleMaps (Only Breakdown postion)",
        streetLocation: manualAddress ?? address ?? "",
      };

      try {
        const response = await fetchWithAuth(`${API_BASE}/api/eagle/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          if (window.opener) {
            window.close();
          } else {
            alert("✅ Breakdown location sent to Eagle.");
          }
        } else {
          const err = await response.text();
          alert("❌ Failed to send to Eagle:\n" + err);
        }
      } catch (e: any) {
        alert("❌ Error sending to Eagle:\n" + e.message);
      }

      setIsSendingToEagle(false);
      return; // DO NOT run workshop workflow
    }

    // 1) Draw the route immediately so users can see it
    //    (we'll reuse this polyline for the static map later)
    const precomputedPolyline = await drawRouteToWorkshop();

    // 2) Show the “sending soon” toast and start the 5s timer
    setShowEagleToast(true);
    setTimeout(() => {
      if (eagleToastRef.current) eagleToastRef.current.showToast();
    }, 0);

    // 3) After 5 seconds, actually push to Eagle
    setTimeout(async () => {
      const selectedWorkshop = workshops.find(
        (w) => w.dealerId === selectedDealerId
      );
      if (!selectedWorkshop) {
        alert("Selected workshop not found.");
        setShowEagleToast(false);
        setTimeout(() => eagleToastRef.current?.hideToast(), 100);
        return;
      }

      // Ensure we have distance/duration for the selected workshop
      const distanceKey = getDistanceKey(
        { lat: position.gpsLatitude, lng: position.gpsLongitude },
        { lat: selectedWorkshop.latitude, lng: selectedWorkshop.longitude }
      );

      let finalDistance = selectedWorkshop.distanceText;
      let finalDuration = selectedWorkshop.durationText;

      if (!distanceCache.current[distanceKey]) {
        const service = new window.google.maps.DistanceMatrixService();
        try {
          const response =
            await new Promise<google.maps.DistanceMatrixResponse>(
              (resolve, reject) => {
                service.getDistanceMatrix(
                  {
                    origins: [
                      {
                        lat: selectedWorkshop.latitude,
                        lng: selectedWorkshop.longitude,
                      },
                    ],
                    destinations: [
                      { lat: position.gpsLatitude, lng: position.gpsLongitude },
                    ],
                    travelMode: google.maps.TravelMode.DRIVING,
                  },
                  (response, status) => {
                    if (
                      status === "OK" &&
                      response?.rows?.[0]?.elements?.[0]?.distance?.text
                    ) {
                      resolve(response);
                    } else {
                      reject(status);
                    }
                  }
                );
              }
            );
          const el = response.rows[0].elements[0];
          finalDistance = el.distance.text;
          distanceCache.current[distanceKey] = {
            distanceText: el.distance.text,
            distanceValue: el.distance.value,
            durationText: el.duration?.text,
          };
        } catch (err) {
          console.warn("⚠️ Could not calculate distance:", err);
        }
      } else {
        finalDistance = distanceCache.current[distanceKey].distanceText;
        finalDuration = distanceCache.current[distanceKey].durationText;
      }

      // Use the already drawn route’s polyline if available; otherwise try once more
      let encodedPolyline = precomputedPolyline;
      if (!encodedPolyline) {
        // fall back: try to read from the current directionsResult or redraw
        encodedPolyline =
          directionsResult?.routes?.[0]?.overview_polyline ??
          (await drawRouteToWorkshop());
      }

      let staticMapUrl = "";
      if (encodedPolyline) {
        staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?size=600x400&format=png32&path=enc:${encodedPolyline}&markers=color:red%7Clabel:T%7C${position.gpsLatitude},${position.gpsLongitude}&markers=color:blue%7Clabel:W%7C${selectedWorkshop.latitude},${selectedWorkshop.longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
      }

      const payload = {
        CaseNumber: caseNumber,
        TruckLatitude: position.gpsLatitude,
        TruckLongitude: position.gpsLongitude,
        wsLatitude: selectedWorkshop.latitude,
        wsLongitude: selectedWorkshop.longitude,
        TruckGpsTimestamp: position.gpsTimestamp ?? new Date().toISOString(),
        Workshop: {
          DealerId: selectedWorkshop.dealerId,
          Source: selectedWorkshop.source,
        },
        StaticMapUrl: staticMapUrl,
        Country: selectedWorkshop.countryCode || selectedWorkshop.country || "",
        Distance: finalDistance || "",
        ShortDescription: "Sent from EagleMaps",
        streetLocation: manualAddress ?? address ?? "",
        DurationETA: finalDuration || "",
      };

      try {
        const response = await fetchWithAuth(`${API_BASE}/api/eagle/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const canProgrammaticallyClose = !!window.opener;
          if (canProgrammaticallyClose) {
            window.close();
          } else {
            alert("✅ Sent to Eagle. You can now close this tab.");
          }
        } else {
          const error = await response.text();
          alert("❌ Failed to send to Eagle:\n" + error);
        }
      } catch (error: any) {
        alert("❌ Error sending to Eagle:\n" + error.message);
      } finally {
        setShowEagleToast(false);
        setTimeout(() => eagleToastRef.current?.hideToast(), 100);
        //re-enable button after request completes
        setIsSendingToEagle(false);
      }
    }, 5000); // delay for 5 seconds
  };

  // Minimal auth guard: hide app UI until authentication is ready and user is logged in (or login is in progress)
  if (!ready || (!account && inProgress === "none")) {
    // Show a spinner from Tegel
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f7f7f7",
        }}
      >
        <TdsSpinner size="md" variant="standard" />
      </div>
    );
  }

  const activeFilterCount =
    selectedTypes.length +
    selectedServices.length +
    selectedSources.length +
    selectedFuels.length +
    (showOnlyOpenNow ? 1 : 0);

  return (
    <>
      <Header />
      <div
        ref={layoutRef}
        style={{ display: "flex", height: "calc(100vh - 64px)" }}
      >
        {/* Sidebar */}
        <div
          style={{
            width: `${sidebarWidth}px`,
            padding: "0.75rem 0.75rem 0 0.75rem",
            background: "#f5f5f5",
            fontFamily: "Roboto, Arial, sans-serif",
            overflowY: "auto",
            fontSize: "13px",
            borderRight: "1px solid #e6e6e6",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Position Failure Warning */}
          {positionFetchFailed && (
            <div
              style={{
                backgroundColor: "#ffc107",
                padding: "8px 12px",
                marginBottom: "12px",
                borderRadius: "4px",
                color: "#333",
                fontWeight: "bold",
                fontSize: "14px",
                position: "relative",
              }}
            >
              ⚠️ Failed to fetch position data. Try again or use the address field to
              manually enter
              <button
                onClick={() => setPositionFetchFailed(false)}
                style={{
                  position: "absolute",
                  top: 1,
                  right: 1,
                  background: "transparent",
                  border: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                  color: "#333",
                }}
                aria-label="Close warning"
              >
                ✖
              </button>
            </div>
          )}

          {/* Collapsible Truck Info Section (hover to peek, click to pin) */}
          <div
            style={{ marginBottom: "0.6rem" }}
            onMouseEnter={() => setTruckHovering(true)}
            onMouseLeave={() => setTruckHovering(false)}
          >
            <div
              onClick={() => setTruckInfoOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "8px 12px",
                boxShadow: "0 2px 3px rgba(0,0,0,0.05)",
                fontWeight: 600,
                fontSize: "14px",
                userSelect: "none",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span>
                  Product Info
                  {vinFromUrl
                    ? ` — ${vinFromUrl}`
                    : position?.externalEquipmentReference
                    ? ` — ${position.externalEquipmentReference}`
                    : ""}
                </span>
                {truckInfoOpen && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#005fcc",
                      lineHeight: 1,
                    }}
                    aria-label="Pinned open"
                    title="Pinned open"
                  >
                    📌
                  </span>
                )}
              </span>
              <span style={{ fontSize: "1.1rem", color: "#002663" }}>
                {truckInfoVisible ? "▲" : "▼"}
              </span>
            </div>

            {truckInfoVisible && (
              <div style={{ marginTop: "0.5rem" }}>
                <TruckInfoCard
                  key={`${position?.gpsLatitude ?? "-"}_${
                    position?.gpsLongitude ?? "-"
                  }`}
                  vin={
                    vinFromUrl ??
                    position?.externalEquipmentReference ??
                    "Unknown"
                  }
                  gpsTimestamp={position?.gpsTimestamp ?? null}
                  address={manualAddress ?? address ?? "Unknown"}
                  caseNumber={caseNumber}
                  isDistanceCalculated={distanceCalculated}
                  latitude={position?.gpsLatitude ?? null}
                  longitude={position?.gpsLongitude ?? null}
                  onManualGpsUpdate={(lat, lng) => {
                    // Clear workshop selection/route so we recalc from the new location
                    setSelectedDealerId(null);
                    setExpandedDealerId(null);
                    setDirectionsResult(null);

                    // Update position and timestamp
                    setPosition((prev) =>
                      prev
                        ? {
                            ...prev,
                            gpsLatitude: lat,
                            gpsLongitude: lng,
                            gpsTimestamp: new Date().toISOString(),
                          }
                        : {
                            gpsLatitude: lat,
                            gpsLongitude: lng,
                            externalEquipmentReference: vinFromUrl ?? "Manual",
                            gpsTimestamp: new Date().toISOString(),
                          }
                    );

                    previousWorkshopState.current = null; // Force refetch
                    setDistanceCalculated(false);

                    // Reverse geocode to refresh address label
                    if (window.google?.maps?.Geocoder) {
                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode(
                        { location: { lat, lng } },
                        (results, status) => {
                          if (
                            status === "OK" &&
                            results &&
                            results.length > 0
                          ) {
                            setAddress(results[0].formatted_address);
                            setManualAddress(null);
                          } else {
                            setAddress("Unknown");
                          }
                        }
                      );
                    } else {
                      setAddress("Unknown");
                    }

                    // Center map on the new coordinates
                    mapRef.current?.panTo({ lat, lng });
                  }}
                />
              </div>
            )}
          </div>

          <ActionButtons
            fetchPositionLocation={fetchPositionLocation}
            onCalculateDistances={handleCalculateDistancesClick}
            distanceCalculated={distanceCalculated}
            sendToEagle={sendToEagle}
            isSendingToEagle={isSendingToEagle}
            hasWorkshopSelected={!!selectedDealerId}
          />

          {/* Collapsible Filters Section (hover to peek, click to pin) */}
          <div
            style={{ margin: "6px 0 10px" }}
            onMouseEnter={() => setFiltersHovering(true)}
            onMouseLeave={() => setFiltersHovering(false)}
          >
            <div
              onClick={() => setFiltersOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "8px 12px",
                boxShadow: "0 2px 3px rgba(0,0,0,0.05)",
                fontWeight: 600,
                fontSize: "14px",
                userSelect: "none",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      background: "#005fcc",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 600,
                      borderRadius: "10px",
                      padding: "2px 7px",
                      lineHeight: "1",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
                {filtersOpen && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#005fcc",
                      lineHeight: 1,
                    }}
                    aria-label="Pinned open"
                    title="Pinned open"
                  >
                    📌
                  </span>
                )}
              </div>
              <span style={{ fontSize: "1.1rem", color: "#002663" }}>
                {filtersVisible ? "▲" : "▼"}
              </span>
            </div>
            {filterSummary.length > 0 && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "11px",
                  color: "#005fcc",
                  lineHeight: 1.3,
                }}
              >
                {filterSummary.join(" • ")}
              </div>
            )}

            {filtersVisible && (
              <div id="filters-panel" style={{ marginTop: "0.6rem" }}>
                <FiltersPanel
                  selectedTypes={selectedTypes}
                  onChangeTypes={setSelectedTypes}
                  selectedServices={selectedServices}
                  onChangeServices={setSelectedServices}
                  selectedSources={selectedSources}
                  onChangeSources={setSelectedSources}
                  showOnlyOpenNow={showOnlyOpenNow}
                  onToggleOpenNow={setShowOnlyOpenNow}
                  selectedFuels={selectedFuels}
                  onChangeFuels={setSelectedFuels}
                />
                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      setSelectedTypes([]);
                      setSelectedServices([]);
                      setSelectedSources([]);
                      setSelectedFuels([]);
                      setShowOnlyOpenNow(false);
                      searchRadius.current = 250;
                      pageSize.current = 10;
                      previousWorkshopState.current = null;
                      setDistanceCalculated(false);
                      if (position) setPosition({ ...position });
                    }}
                    style={{
                      fontSize: 12,
                      padding: "4px 6px",
                      borderRadius: 4,
                      border: "none",
                      background: "transparent",
                      color: "#005fcc",
                      cursor: "pointer",
                      textDecoration: "underline",
                      minWidth: "auto",
                    }}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Compact search status & controls */}
          {!isLoadingWorkshops && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e6e6e6",
                borderRadius: 6,
                padding: "6px 8px",
                marginBottom: 10,
                lineHeight: 1.3,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
              }}
            >
              <span style={{ color: "#222", fontWeight: 700 }}>Search</span>
              <span style={{ color: "#666" }}>
                Radius: {searchRadius.current} km • Page: {pageSize.current}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button
                  onClick={widenSearch}
                  style={{
                    padding: "5px 8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "#f7f7f7",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Expand (+50 km / +10)
                </button>
                <button
                  onClick={resetSearch}
                  style={{
                    padding: "5px 8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "#fff5f5",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          )}
          <div
            style={{
              minHeight: 0,
              paddingBottom: "0.75rem",
            }}
          >
            {isLoadingWorkshops ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "180px",
                }}
              >
                <TdsSpinner
                  size="md"
                  variant="standard"
                  style={{ marginBottom: 18 }}
                />
                <div
                  style={{
                    color: "#666",
                    fontSize: 18,
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                    marginTop: 4,
                  }}
                >
                  Fetching nearby workshops…
                </div>
              </div>
            ) : (
              <WorkshopList
                workshops={filteredWorkshops}
                expandedId={expandedDealerId}
                selectedId={selectedDealerId}
                pinnedId={pinnedWorkshopId}
                onToggleExpand={(id) => {
                  const workshopChanged = selectedDealerId !== id;
                  setExpandedDealerId((prev) => (prev === id ? null : id));
                  setSelectedDealerId(id);
                  setDirectionsResult(null);
                  if (workshopChanged) {
                    setDistanceCalculated(false);
                  }
                }}
                refreshUI={refreshUI}
                clearSelection={() => {
                  setSelectedDealerId(null);
                  setExpandedDealerId(null);
                  setDirectionsResult(null);
                  setDistanceCalculated(false);
                }}
                totalCount={rawWorkshops.length}
              />
            )}
          </div>
        </div>

        {/* Resizer */}
        <div
          onMouseDown={onResizerMouseDown}
          className="sidebar-resizer"
          style={{
            width: "6px",
            cursor: "col-resize",
            background: "transparent",
          }}
        />

        {/* Map + Floating UI */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}
            libraries={googleMapLibraries}
            onLoad={() => setMapsReady(true)}
            onError={(e) => {
              console.error("❌ Google Maps JS failed to load", e);
              setMapsReady(false);
            }}
          >
            {/* Floating address bar */}
            <div
              style={{
                position: "absolute",
                top: "20px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 100,
                width: "400px",
                maxWidth: "90%",
              }}
            >
              <Autocomplete
                onLoad={(autocomplete) =>
                  (autocompleteRef.current = autocomplete)
                }
                onPlaceChanged={() => {
                  previousWorkshopState.current = null;
                  if (!autocompleteRef.current) return;

                  //clear selection & any route, and re-enable the button
                  setSelectedDealerId(null);
                  setExpandedDealerId(null);
                  setDirectionsResult(null);
                  setDistanceCalculated(false);

                  const place = autocompleteRef.current.getPlace();
                  if (!place || !place.geometry || !place.geometry.location) {
                    console.warn(
                      "❌ Invalid or incomplete place. Please select a valid suggestion."
                    );
                    return;
                  }

                  const location = place.geometry.location;
                  const formatted = place.formatted_address;

                  if (location && formatted) {
                    const lat = location.lat();
                    const lng = location.lng();

                    setManualAddress(formatted);
                    setPosition((prev) =>
                      prev
                        ? { ...prev, gpsLatitude: lat, gpsLongitude: lng }
                        : {
                            gpsLatitude: lat,
                            gpsLongitude: lng,
                            externalEquipmentReference: vinFromUrl ?? "Manual",
                            gpsTimestamp: new Date().toISOString(),
                          }
                    );

                    mapRef.current?.panTo({ lat, lng });
                  }
                }}
              >
                <input
                  type="text"
                  value={
                    manualAddress ??
                    (address && address !== "Unknown" ? address : "")
                  }
                  onChange={(e) => setManualAddress(e.target.value)}
                  placeholder="Enter truck address"
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    fontSize: "14px",
                    borderRadius: "24px",
                    border: "1px solid #ccc",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    outline: "none",
                    fontFamily: "Roboto, sans-serif",
                  }}
                />
              </Autocomplete>
            </div>

            {/* Map panel position toast */}
            {showPositionToast && (
              <div
                style={{
                  position: "absolute",
                  bottom: 32,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 9999,
                  minWidth: 340,
                  maxWidth: "90vw",
                  pointerEvents: "none",
                }}
              >
                <TdsToast
                  ref={positionToastRef}
                  variant="information"
                  header="Fetching latest vehicle position"
                  toast-role="status"
                  style={{
                    opacity: 0.98,
                    pointerEvents: "auto",
                    boxShadow: "0 6px 24px 0 rgba(0,0,0,0.22)",
                  }}
                />
              </div>
            )}
            {/* Eagle delay toast */}
            {showEagleToast && (
              <div
                style={{
                  position: "absolute",
                  bottom: 72,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 9999,
                  minWidth: 340,
                  maxWidth: "90vw",
                  pointerEvents: "none",
                }}
              >
                <TdsToast
                  ref={eagleToastRef}
                  variant="information"
                  header="Preparing route for Eagle"
                  subheader="Sending in 5 seconds..."
                  toast-role="status"
                  style={{
                    opacity: 0.98,
                    pointerEvents: "auto",
                    boxShadow: "0 6px 24px 0 rgba(0,0,0,0.22)",
                  }}
                />
              </div>
            )}
            {/* Workshop not found toast */}
            {showWorkshopNotFoundToast && (
              <div
                style={{
                  position: "absolute",
                  top: 92,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10000,
                  minWidth: 340,
                  maxWidth: "90vw",
                  pointerEvents: "none",
                }}
              >
                <TdsToast
                  ref={notFoundToastRef}
                  variant="warning"
                  header="Selected workshop not found nearby"
                  subheader="Please select a workshop from the available list"
                  toast-role="status"
                  style={{
                    opacity: 0.98,
                    pointerEvents: "auto",
                    boxShadow: "0 6px 24px 0 rgba(0,0,0,0.22)",
                  }}
                  onTdsClose={() => setShowWorkshopNotFoundToast(false)}
                />
              </div>
            )}
            {/* Failed fetch toast */}
            {showFailedFetchToast && (
              <div
                style={{
                  position: "absolute",
                  top: 140,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10000,
                  minWidth: 340,
                  maxWidth: "90vw",
                  pointerEvents: "none",
                }}
              >
                <TdsToast
                  ref={failedFetchToastRef}
                  variant="warning"
                  header="Some data failed to load"
                  subheader="Showing partial results. Try again later."
                  toast-role="status"
                  style={{
                    opacity: 0.98,
                    pointerEvents: "auto",
                    boxShadow: "0 6px 24px 0 rgba(0,0,0,0.22)",
                  }}
                  onTdsClose={() => setShowFailedFetchToast(false)}
                />
              </div>
            )}
            {/* Map itself */}
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              zoom={10}
              center={computedCenter ?? defaultCenter}
              onLoad={(map) => {
                mapRef.current = map;
              }}
              options={{
                zoomControl: true,
                styles: highwayStyles,
              }}
              onRightClick={handleRightClick}
            >
              {position && (
                <TruckMarker
                  position={{
                    lat: position.gpsLatitude,
                    lng: position.gpsLongitude,
                  }}
                  icon={svgTruck()} // 👈 call it as a function
                  mapsReady={mapsReady}
                  hidden={!!directionsResult}
                />
              )}

              {/* Clustered and dynamically sized workshop markers */}
              {mapsReady &&
                !directionsResult &&
                (() => {
                  // Render all NON-selected markers inside the clusterer
                  const nonSelectedMarkers = (
                    <MarkerClustererF>
                      {(clusterer) => (
                        <>
                          {jitteredWorkshops
                            .filter((w) => w.dealerId !== selectedDealerId)
                            .map((dealer) => {
                              return (
                                <Marker
                                  key={dealer.dealerId}
                                  position={{
                                    lat: dealer.latitude,
                                    lng: dealer.longitude,
                                  }}
                                  clusterer={clusterer}
                                  icon={(() => {
                                    const base = iconForSource(dealer.source);
                                    // Match truck icon size exactly
                                    return {
                                      ...base,
                                      // Match truck icon size exactly
                                      scale: 2,
                                    } as google.maps.Symbol;
                                  })()}
                                  label={{
                                    text: dealer.name ?? "",
                                    className: "ws-label",
                                  }}
                                  opacity={0.65}
                                  onClick={() => {
                                    setExpandedDealerId(dealer.dealerId);
                                    setSelectedDealerId(dealer.dealerId);
                                    setDirectionsResult(null);
                                    setDistanceCalculated(false);
                                  }}
                                  title={`${dealer.name}${
                                    dealer.city ? " – " + dealer.city : ""
                                  }`} // workshop name and city on hover
                                />
                              );
                            })}
                        </>
                      )}
                    </MarkerClustererF>
                  );

                  // Render the SELECTED marker outside the clusterer so it always shows as a single icon
                  const selected = jitteredWorkshops.find(
                    (w) => w.dealerId === selectedDealerId
                  );
                  const selectedMarker = selected ? (
                    <React.Fragment key={`selected-${selected.dealerId}`}>
                      <Marker
                        position={{
                          lat: selected.latitude,
                          lng: selected.longitude,
                        }}
                        icon={(() => {
                          const base = iconForSource(selected.source);
                          // Enlarge selected marker vs truck for emphasis
                          return {
                            ...base,
                            scale: 3,
                          } as google.maps.Symbol;
                        })()}
                        label={{
                          text: selected.name ?? "",
                          className: "ws-label ws-label--selected",
                        }}
                        opacity={1}
                        zIndex={999}
                        onClick={() => {
                          setExpandedDealerId(selected.dealerId);
                          setSelectedDealerId(selected.dealerId);
                        }}
                        title={`${selected.name}${
                          selected.city ? " – " + selected.city : ""
                        }`}
                      />
                      <OverlayView
                        position={{
                          lat: selected.latitude,
                          lng: selected.longitude,
                        }}
                        mapPaneName={OverlayView.FLOAT_PANE}
                      >
                        <div className="pulse-marker" />
                      </OverlayView>
                    </React.Fragment>
                  ) : null;

                  return (
                    <>
                      {nonSelectedMarkers}
                      {selectedMarker}
                    </>
                  );
                })()}

              {/* Route */}
              {directionsResult && (
                <DirectionsRenderer
                  directions={directionsResult}
                  options={{
                    polylineOptions: {
                      strokeColor: "#002663",
                      strokeOpacity: 0.9,
                      strokeWeight: 5,
                    },
                    suppressMarkers: false,
                  }}
                />
              )}

              {/* Confirm move popup */}
              {pendingMove && (
                <InfoWindow
                  position={{ lat: pendingMove.lat(), lng: pendingMove.lng() }}
                  onCloseClick={cancelMove}
                >
                  <div style={{ fontSize: "14px", lineHeight: 1.5 }}>
                    <p style={{ marginBottom: "0.5rem" }}>
                      Move truck here?
                      <br />
                      <strong>Lat:</strong> {pendingMove.lat().toFixed(5)}
                      <br />
                      <strong>Lng:</strong> {pendingMove.lng().toFixed(5)}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        onClick={confirmMove}
                        style={{
                          padding: "6px 10px",
                          backgroundColor: "#28a745",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Yes
                      </button>
                      <button
                        onClick={cancelMove}
                        style={{
                          padding: "6px 10px",
                          backgroundColor: "#dc3545",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </LoadScript>
        </div>
      </div>
    </>
  );
}

export default App;
